import { RootState } from '../../../app/store';
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import FixtureInputModel from '../models/FixtureInputModel';
import { MarketInfoType } from '../../MarketInfo/models/MarketInfoQueryModel';
import CommissionItemModel from '../components/FixtureCommissionDetail/models/CommissionItemModel';

interface FixtureInputState {
    input: FixtureInputModel,
    selectedFixtureId: string,
    fixtureInputExpand: boolean,
    triggerFocusTonnageInput: number,
    triggerSave: number,
    triggerRefreshInput: number,
    showInputPanel: boolean,
    hideInvoice: boolean,
    hideEva: boolean,
    currentChartererName: string,
    isEdit: boolean
}

const initialState: FixtureInputState = {
    input: {
        id: "",
        ref: "",
        bizType: MarketInfoType.Voy,
        tonnageId: "",
        ownerId: "",
        ownerBrokerId: null,
        chartererId: "",
        chartererBrokerId: null,
        quantityOrDuration: 0,
        rate: 0,
        estdComm: 0,
        office: "",
        cpDate: "",
        collectFrom: "",
        privateCommRemark: "",
        initEvaVolume: 0,
        initEvaComplex: 0,
        remarks: "",
        commRateDetail: JSON.stringify([{ index: 0, brokerId: null, percent: 0 } as CommissionItemModel])
    } as FixtureInputModel,
    selectedFixtureId: "",
    fixtureInputExpand: true,
    triggerFocusTonnageInput: 0,
    triggerSave: 0,
    triggerRefreshInput: 0,
    showInputPanel: false,
    hideInvoice: true,
    hideEva: true,
    currentChartererName: "",
    isEdit: false
}

export const FixtureInputSlice = createSlice({
    name: "FixtureInput",
    initialState,
    reducers: {
        setFixtureInputId: (state, action: PayloadAction<string>) => {
            state.input.id = action.payload;
        },
        setFixtureInput: (state, action: PayloadAction<FixtureInputModel>) => {
            state.input = action.payload;
        },
        setRef: (state, action: PayloadAction<string>) => {
            state.input.ref = action.payload;
        },
        setBizType: (state, action: PayloadAction<string>) => {
            state.input.bizType = action.payload;
        },
        setTonnageId: (state, action: PayloadAction<string>) => {
            state.input.tonnageId = action.payload;
        },
        setOwnerId: (state, action: PayloadAction<string>) => {
            state.input.ownerId = action.payload;
        },
        setOwnerBrokerId: (state, action: PayloadAction<number | null>) => {
            state.input.ownerBrokerId = action.payload;
        },
        setChartererId: (state, action: PayloadAction<string>) => {
            state.input.chartererId = action.payload;
        },
        setChartererBrokerId: (state, action: PayloadAction<number | null>) => {
            state.input.chartererBrokerId = action.payload;
        },
        setQuantityOrDuration: (state, action: PayloadAction<number>) => {
            state.input.quantityOrDuration = action.payload;
        },
        setRate: (state, action: PayloadAction<number>) => {
            state.input.rate = action.payload;
        },
        setEstdComm: (state, action: PayloadAction<number>) => {
            state.input.estdComm = action.payload;
        },
        setOffice: (state, action: PayloadAction<string>) => {
            state.input.office = action.payload;
        },
        setSupport: (state, action: PayloadAction<number>) => {
            state.input.support = action.payload;
        },
        setCpDate: (state, action: PayloadAction<string>) => {
            state.input.cpDate = action.payload;
        },
        setCollectFrom: (state, action: PayloadAction<string>) => {
            state.input.collectFrom = action.payload;
        },
        setPrivateCommRemark: (state, action: PayloadAction<string>) => {
            state.input.privateCommRemark = action.payload;
        },
        setBankAccount: (state, action: PayloadAction<string>) => {
            state.input.privateCommRemark = action.payload;
        },
        setBankReceived: (state, action: PayloadAction<string>) => {
            state.input.privateCommRemark = action.payload;
        },
        setReceivedDate: (state, action: PayloadAction<string>) => {
            state.input.privateCommRemark = action.payload;
        },
        setInitEvaVolume: (state, action: PayloadAction<number>) => {
            state.input.initEvaVolume = action.payload;
        },
        setInitEvaComplex: (state, action: PayloadAction<number>) => {
            state.input.initEvaComplex = action.payload;
        },
        setRemarks: (state, action: PayloadAction<string>) => {
            state.input.remarks = action.payload;
        },
        setCommRateDetail: (state, action: PayloadAction<string>) => {
            state.input.commRateDetail = action.payload;
        },
        setSelectedFixtureId: (state, action: PayloadAction<string>) => {
            state.selectedFixtureId = action.payload;
        },
        setFixtureInputExpand: (state, action: PayloadAction<boolean>) => {
            state.fixtureInputExpand = action.payload;
        },
        setTriggerFocusChartererInput: (state, action: PayloadAction<number>) => {
            state.triggerFocusTonnageInput = action.payload;
        },
        resetFixtureInput: (state) => {
            state.input = initialState.input;
        },
        setTriggerSave: (state, action: PayloadAction<number>) => {
            state.triggerSave = action.payload;
        },
        setTriggerRefreshInput: (state, action: PayloadAction<number>) => {
            state.triggerRefreshInput = action.payload;
        },
        setShowInputPanel: (state, action: PayloadAction<boolean>) => {
            state.showInputPanel = action.payload;
        },
        setTonnageVesselName: (state, action: PayloadAction<string>) => {
            state.input.tonnageVesselName = action.payload;
        },
        setHideInvoice: (state, action: PayloadAction<boolean>) => {
            state.hideInvoice = action.payload;
        },
        setHideEva: (state, action: PayloadAction<boolean>) => {
            state.hideEva = action.payload;
        },
        setCurrentChartererName: (state, action: PayloadAction<string>) => {
            state.currentChartererName = action.payload;
        },
        setIsEditFixture: (state, action: PayloadAction<boolean>) => {
            state.isEdit = action.payload;
        },
    }
});

export const FixtureInputSelector = createSelector(
    (state: RootState) => state.fixtureInput,
    (data) => data
);

export const {
    setFixtureInputId,
    setFixtureInput,
    setSelectedFixtureId,
    setFixtureInputExpand,
    setTriggerFocusChartererInput,
    resetFixtureInput,
    setTriggerSave,
    setTriggerRefreshInput,
    setRef,
    setBizType,
    setTonnageId,
    setOwnerId,
    setSupport,
    setOwnerBrokerId,
    setChartererId,
    setChartererBrokerId,
    setQuantityOrDuration,
    setRate,
    setCpDate,
    setCollectFrom,
    setPrivateCommRemark,
    setEstdComm,
    setOffice,
    setBankAccount,
    setBankReceived,
    setReceivedDate,
    setInitEvaVolume,
    setInitEvaComplex,
    setRemarks,
    setCommRateDetail,
    setShowInputPanel,
    setHideInvoice,
    setHideEva,
    setTonnageVesselName,
    setCurrentChartererName,
    setIsEditFixture
} = FixtureInputSlice.actions;

export default FixtureInputSlice.reducer;